/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessage, defineMessages} from 'react-intl'

// Global app defaults
export const DEFAULT_LOCALE = 'en-US'
export const DEFAULT_SITE_TITLE = defineMessage({
    id: 'global.page.default_site_title',
    defaultMessage: 'Children´s High Chair, Strollers & Nursery'
})
export const MAX_CACHE_AGE = 60 * 15 // 15 min
export const STALE_WHILE_REVALIDATE = 60 * 15 // 15 min

// Constants used in the used for product searching.
export const DEFAULT_CATEGORY_PARAMS = {limit: 38, offset: 0, sort: 'best-matches', refine: []}
export const DEFAULT_SEARCH_PARAMS = {limit: 12, offset: 0, sort: 'best-matches', refine: []}
export const DEFAULT_LIMIT_VALUES = [25, 50, 100] // Page sizes

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5
export const RECENT_SEARCH_KEY = 'recent-search-key'
export const RECENT_SEARCH_MIN_LENGTH = 3

// PDP display template types
export const CUSTOMIZABLE_SET_DISPLAY_TEMPLATE = 'customizable-set'

// Constants for the Homepage's Shop Products section.
export const HOME_SHOP_PRODUCTS_CATEGORY_ID = 'newarrivals'
export const HOME_SHOP_PRODUCTS_LIMIT = 10

// Constant for the my account orders section.
export const MY_ACCOUNT_ORDERS_LIMIT = 50

// Constants for menu
export const CAT_MENU_DEFAULT_NAV_DEPTH = 1
export const CAT_MENU_DEFAULT_ROOT_CATEGORY = 'root'
export const CAT_MENU_STALE_TIME = 10000 // 10 seconds

// Constants used for differentiation of customer types.
export const CUSTOMER_TYPE_B2C = 'b2c'
export const CUSTOMER_TYPE_D2B = 'd2b'

// Color to use for the UI surrounding the page in browsers.
export const THEME_COLOR = '#ffffff'

export const FILTER_ACCORDION_SATE = 'filters-expanded-index'

export const LOGIN_INACTIVE_ERROR_MESSAGE = defineMessage({
    defaultMessage:
        'In order to login, you have to activate your account from the link we sent you in an email.',
    id: 'account-registration.msg.double_opt_in'
})

export const API_ERROR_MESSAGE = defineMessage({
    id: 'global.error.something_went_wrong',
    defaultMessage: 'Hmmm! Something went wrong. Please try again.'
})
export const INVALID_TOKEN_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'Invalid token, please try again.',
    id: 'global.error.invalid_token'
})
export const FEATURE_UNAVAILABLE_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'This feature is not currently available.',
    id: 'global.error.feature_unavailable'
})
export const CREATE_ACCOUNT_FIRST_ERROR_MESSAGE = defineMessage({
    defaultMessage:
        'This feature is not currently available. You must create an account to access this feature.',
    id: 'global.error.create_account'
})

// route constants
export const HOME_HREF = '/home'
export const CART_HREF = '/appcart'
export const CPSIA_HREF = '/cpsia'
export const SIGN_IN_HREF = '/login'
export const REGISTRATION_HREF = '/registration'
export const RESET_PASSWORD_HREF = '/reset-password'
export const STORE_LOCATOR_HREF = '/stores'
export const ACCOUNT_HREF = '/my-stokke'
export const CHECKOUT_HREF = '/appcheckout'
export const COUNTRY_SELECTOR_HREF = '/country-selector'
export const CONTACT_US_HREF = '/contact-us'
export const CONTACT_LANDING_HREF = '/customer-service/customer-service-contact-form.html'
export const FAQ_HREF = '/customer-service/faq/faq.html'
export const CUSTOMER_SERVICE_HREF = '/customer-service'
export const WARRANTY_REGISTER_HREF = '/warranty'
export const WISHLIST_HREF = '/my-stokke/wishlist'
export const urlPartPositions = {
    PATH: 'path',
    QUERY_PARAM: 'query_param',
    NONE: 'none'
}
export const PDP_ROUTE_PATH = '/*/:productId.html'
export const PLP_ROUTE_PATH = '/:categoryPath'

export const MAX_ORDER_QUANTITY = 10

// Toast messages exist outside the scope of the base IntlProvider. This means
// that commonly used components that require localization cannot easily be
// extracted into a hook/helper. However, we can still extract the message
// definitions to a common location (here), so that each message is only defined
// once.

export const TOAST_MESSAGE_MERGE_WISHLIST = defineMessage({
    id: 'global.info.merge_wishlist',
    defaultMessage: 'Your wishlist has been transferred to your account wishlist on My Stokke'
})

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
    id: 'global.info.added_to_wishlist',
    defaultMessage: '{quantity} {quantity, plural, one {item} other {items}} added to wishlist'
})

export const TOAST_ACTION_VIEW_WISHLIST = defineMessage({
    defaultMessage: 'View',
    id: 'global.link.added_to_wishlist.view_wishlist'
})

export const TOAST_MESSAGE_REMOVED_FROM_WISHLIST = defineMessage({
    id: 'global.info.removed_from_wishlist',
    defaultMessage: 'Item removed from wishlist'
})

export const ACCOUNT_REGISTRATION_FORM_DATA = 'account_registration_data'

export const ADYEN_CONFIGURATION = 'adyen_configuration'

export const CHECKOUT_TOKEN = 'checkout_token'
export const CHECKOUT_ORDER_ID = 'checkout_order_id'

export const FAILED_BASKET_ID = 'failed_basket_id'

export const FAIL_ORDER_ERROR_MESSAGE = defineMessage({
    id: 'global.error.payment.failed',
    defaultMessage: 'Order was cancelled because payment has failed/refused'
})

// Delay before scrolling up to payments, will be removed if we get event to do the same from Adyen
export const PAYMENT_CHALLENGE_DELAY = 2000

export const GO_INSTORE_BASE_URL = 'https://gis.goinstore.com/gis/track/purchase'

// used in case of selection of back button that does not reload the previous page
export const PAGE_SHOW_LISTENER = function (event) {
    if (event.persisted) {
        window.location.reload()
    }
}

// used for cms scripts to be executed on page load
export const CMS_SCRIPTS_MIME_TYPE = 'application/x-pwa-cms-script'

// used to predefine MY-STOKKE headline and subline
export const MY_STOKKE_CONFIG_LABELS = {
    addresses: {
        headline: defineMessage({
            id: 'mystokke.addresses.headline',
            defaultMessage: 'Addresses'
        }),
        subline: defineMessage({
            id: 'mystokke.addresses.subline',
            defaultMessage: ''
        })
    },
    orders: {
        headline: defineMessage({
            id: 'mystokke.orders.headline',
            defaultMessage: 'Orders'
        }),
        subline: defineMessage({
            id: 'mystokke.orders.subline',
            defaultMessage: ''
        })
    },
    'personal-information': {
        headline: defineMessage({
            id: 'mystokke.personal-information.headline',
            defaultMessage: 'Personal information'
        }),
        subline: defineMessage({
            id: 'mystokke.personal-information.subline',
            defaultMessage: ''
        })
    },
    warranty: {
        headline: defineMessage({
            id: 'mystokke.warranty.headline',
            defaultMessage: 'Product Registration & Warranties'
        }),
        subline: defineMessage({
            id: 'mystokke.warranty.subline',
            defaultMessage: 'Register your Stokke products to activate or extend your warranty.'
        })
    },
    wishlist: {
        headline: defineMessage({
            id: 'mystokke.wishlist.headline',
            defaultMessage: 'Wishlist'
        }),
        subline: defineMessage({
            id: 'mystokke.wishlist.subline',
            defaultMessage: ''
        })
    }
}

export const MY_STOKKE_CONFIG_LABELS_D2B = {
    ...MY_STOKKE_CONFIG_LABELS,
    'personal-information': {
        headline: defineMessage({
            id: 'mystokke.company-information.headline',
            defaultMessage: 'Company information'
        }),
        subline: defineMessage({
            id: 'mystokke.company-information.subline',
            defaultMessage: 'Manage company details here.'
        })
    }
}

// used for address field salutations
export const SALUTATION_OPTIONS = [
    {
        value: 'Mr',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option1',
            defaultMessage: 'Mr'
        }),
        display: true
    },
    {
        value: 'Mrs',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option2',
            defaultMessage: 'Mrs'
        }),
        display: true
    },
    {
        value: 'Other',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option5',
            defaultMessage: 'Other'
        }),
        display: false
    }
]

// used for point of purchase options
export const POINTOFPURCHASE_OPTIONS = {
    store: {
        label: defineMessage({
            id: 'warranty.pop.in_store',
            defaultMessage: 'In a store'
        }),
        display: true
    },
    online: {
        label: defineMessage({
            id: 'warranty.pop.online',
            defaultMessage: 'Online'
        }),
        display: true
    },
    gift: {
        label: defineMessage({
            id: 'warranty.pop.gift',
            defaultMessage: 'Gift'
        }),
        display: true
    }
}

// Used by Locale Selector which needs the country/language pairs
export const LOCALE_NAMES = {
    'da-DK': 'Dansk',
    'de-AT': 'Deutsch',
    'de-CH': 'Deutsch',
    'de-DE': 'Deutsch',
    'de-LU': 'Deutsch',
    'en-AE': 'English',
    'en-AL': 'English',
    'en-AM': 'English',
    'en-AU': 'English',
    'en-AZ': 'English',
    'en-BA': 'English',
    'en-BG': 'English',
    'en-BY': 'English',
    'en-CA': 'English',
    'en-CY': 'English',
    'en-CZ': 'English',
    'en-EE': 'English',
    'en-EG': 'English',
    'en-FI': 'English',
    'en-GB': 'English',
    'en-GE': 'English',
    'en-GR': 'English',
    'en-HK': 'English',
    'en-HR': 'English',
    'en-HU': 'English',
    'en-IE': 'English',
    'en-IL': 'English',
    'en-IN': 'English',
    'en-IR': 'English',
    'en-IS': 'English',
    'en-LT': 'English',
    'en-LV': 'English',
    'en-MY': 'English',
    'en-NZ': 'English',
    'en-PT': 'English',
    'en-QA': 'English',
    'en-RO': 'English',
    'en-RS': 'English',
    'en-SG': 'English',
    'en-SI': 'English',
    'en-SK': 'English',
    'en-US': 'English',
    'en-ZA': 'English',
    'es-AR': 'Español',
    'es-CL': 'Español',
    'es-CO': 'Español',
    'es-CR': 'Español',
    'es-ES': 'Español',
    'es-MX': 'Español',
    'es-PA': 'Español',
    'es-PE': 'Español',
    'es-PR': 'Español',
    'fr-BE': 'Français',
    'fr-CH': 'Français',
    'fr-FR': 'Français',
    'fr-LU': 'Français',
    'it-CH': 'Italiano',
    'it-IT': 'Italiano',
    'ja-JP': '日本語',
    'ko-KR': '한국어',
    'nl-BE': 'Nederlands',
    'nl-NL': 'Nederlands',
    'no-NO': 'Norsk',
    'pl-PL': 'Polski',
    'pt-BR': 'Português',
    'ru-BY': 'Русский',
    'ru-KZ': 'Русский',
    'ru-RU': 'Русский',
    'en-UA': 'Ukraine',
    'sv-SE': 'Svenska',
    'tr-TR': 'Türk',
    'zh-CN': '简体中文',
    'zh-HK': '香港粵語',
    'zh-TW': '國語'
}

export const COUNTRY_MESSAGES = defineMessages({
    AE: {id: 'country.ae', defaultMessage: 'United Arab Emirates'},
    AL: {id: 'country.al', defaultMessage: 'Albania'},
    AM: {id: 'country.am', defaultMessage: 'Armenia'},
    AR: {id: 'country.ar', defaultMessage: 'Argentina'},
    AT: {id: 'country.at', defaultMessage: 'Austria'},
    AU: {id: 'country.au', defaultMessage: 'Australia'},
    AZ: {id: 'country.az', defaultMessage: 'Azerbaijan'},
    BA: {id: 'country.ba', defaultMessage: 'Bosnia and Herzegovina'},
    BE: {id: 'country.be', defaultMessage: 'Belgium'},
    BG: {id: 'country.bg', defaultMessage: 'Bulgaria'},
    BN: {id: 'country.bn', defaultMessage: 'Brunei'},
    BR: {id: 'country.br', defaultMessage: 'Brazil'},
    BY: {id: 'country.by', defaultMessage: 'Belarus'},
    CA: {id: 'country.ca', defaultMessage: 'Canada'},
    CH: {id: 'country.ch', defaultMessage: 'Switzerland'},
    CL: {id: 'country.cl', defaultMessage: 'Chile'},
    CN: {id: 'country.cn', defaultMessage: 'China'},
    CO: {id: 'country.co', defaultMessage: 'Colombia'},
    CR: {id: 'country.cr', defaultMessage: 'Costa Rica'},
    CY: {id: 'country.cy', defaultMessage: 'Cyprus'},
    CZ: {id: 'country.cz', defaultMessage: 'Czech Republic'},
    DE: {id: 'country.de', defaultMessage: 'Germany'},
    DK: {id: 'country.dk', defaultMessage: 'Denmark'},
    EE: {id: 'country.ee', defaultMessage: 'Estonia'},
    EG: {id: 'country.eg', defaultMessage: 'Egypt'},
    ES: {id: 'country.es', defaultMessage: 'Spain'},
    EU: {id: 'country.eu', defaultMessage: 'Other EU'},
    FI: {id: 'country.fi', defaultMessage: 'Finland'},
    FR: {id: 'country.fr', defaultMessage: 'France'},
    GB: {id: 'country.gb', defaultMessage: 'United Kingdom'},
    GE: {id: 'country.ge', defaultMessage: 'Georgia'},
    GR: {id: 'country.gr', defaultMessage: 'Greece'},
    HK: {id: 'country.hk', defaultMessage: 'Hong Kong'},
    HR: {id: 'country.hr', defaultMessage: 'Croatia'},
    HU: {id: 'country.hu', defaultMessage: 'Hungary'},
    ID: {id: 'country.id', defaultMessage: 'Indonesia'},
    IE: {id: 'country.ie', defaultMessage: 'Ireland'},
    IL: {id: 'country.il', defaultMessage: 'Israel'},
    IN: {id: 'country.in', defaultMessage: 'India'},
    IR: {id: 'country.ir', defaultMessage: 'Iran'},
    IS: {id: 'country.is', defaultMessage: 'Iceland'},
    IT: {id: 'country.it', defaultMessage: 'Italy'},
    JP: {id: 'country.jp', defaultMessage: 'Japan'},
    KG: {id: 'country.kg', defaultMessage: 'Kyrgyzstan'},
    KR: {id: 'country.kr', defaultMessage: 'South Korea'},
    KZ: {id: 'country.kz', defaultMessage: 'Kazakhstan'},
    LT: {id: 'country.lt', defaultMessage: 'Lithuania'},
    LU: {id: 'country.lu', defaultMessage: 'Luxembourg'},
    LV: {id: 'country.lv', defaultMessage: 'Latvia'},
    MX: {id: 'country.mx', defaultMessage: 'Mexico'},
    MY: {id: 'country.my', defaultMessage: 'Malaysia'},
    NL: {id: 'country.nl', defaultMessage: 'Netherlands'},
    NO: {id: 'country.no', defaultMessage: 'Norway'},
    NZ: {id: 'country.nz', defaultMessage: 'New Zealand'},
    PA: {id: 'country.pa', defaultMessage: 'Panama'},
    PE: {id: 'country.pe', defaultMessage: 'Peru'},
    PH: {id: 'country.ph', defaultMessage: 'Phillippines'},
    PL: {id: 'country.pl', defaultMessage: 'Poland'},
    PR: {id: 'country.pr', defaultMessage: 'Puerto Rico'},
    PT: {id: 'country.pt', defaultMessage: 'Portugal'},
    QA: {id: 'country.qa', defaultMessage: 'Qatar'},
    RO: {id: 'country.ro', defaultMessage: 'Romania'},
    RS: {id: 'country.rs', defaultMessage: 'Serbia'},
    RU: {id: 'country.ru', defaultMessage: 'Russia'},
    SE: {id: 'country.se', defaultMessage: 'Sweden'},
    SG: {id: 'country.sg', defaultMessage: 'Singapore'},
    SI: {id: 'country.si', defaultMessage: 'Slovenia'},
    SK: {id: 'country.sk', defaultMessage: 'Slovakia'},
    TH: {id: 'country.th', defaultMessage: 'Thailand'},
    TW: {id: 'country.tw', defaultMessage: 'Taiwan - Greater China'},
    TR: {id: 'country.tr', defaultMessage: 'Turkey'},
    UA: {id: 'country.ua', defaultMessage: 'Ukraine'},
    US: {id: 'country.us', defaultMessage: 'United States'},
    UK: {id: 'country.uk', defaultMessage: 'United Kingdom'},
    XB: {id: 'country.xb', defaultMessage: 'Wonderland'},
    ZA: {id: 'country.za', defaultMessage: 'South Africa'}
})

export const CONTINENT_MESSAGES = defineMessages({
    Africa: {id: 'global.continent.africa', defaultMessage: 'Africa'},
    Asia: {id: 'global.continent.asia', defaultMessage: 'Asia'},
    Europe: {id: 'global.continent.europe', defaultMessage: 'Europe'},
    North_America: {id: 'global.continent.northamerica', defaultMessage: 'North America'},
    Oceania: {id: 'global.continent.oceania', defaultMessage: 'Oceania'},
    South_America: {id: 'global.continent.southamerica', defaultMessage: 'South America'}
})

export const PROMOTION_BADGE_MESSAGES = defineMessages({
    'promo.shortheadline.extra': {
        id: 'promo.shortheadline.extra',
        defaultMessage: 'Extra %'
    },
    'promo.shortheadline.outlet_price': {
        id: 'promo.shortheadline.outlet_price',
        defaultMessage: 'Outlet'
    },
    'promo.shortheadline.outlet_price_10': {
        id: 'promo.shortheadline.outlet_price_10',
        defaultMessage: 'Outlet Price -10%'
    },
    'promo.shortheadline.outlet_price_30': {
        id: 'promo.shortheadline.outlet_price_30',
        defaultMessage: 'Outlet Price -30%'
    },
    'promo.shortheadline.outlet_price_50': {
        id: 'promo.shortheadline.outlet_price_50',
        defaultMessage: 'Outlet Price -50%'
    },
    'promo.shortheadline.last_chance': {
        id: 'promo.shortheadline.last_chance',
        defaultMessage: 'Last Chance'
    },
    'promo.shortheadline.last_chance_10': {
        id: 'promo.shortheadline.last_chance_10',
        defaultMessage: 'Last Chance -10%'
    },
    'promo.shortheadline.last_chance_30': {
        id: 'promo.shortheadline.last_chance_30',
        defaultMessage: 'Last Chance -30%'
    },
    'promo.shortheadline.last_chance_50': {
        id: 'promo.shortheadline.last_chance_50',
        defaultMessage: 'Last Chance -50%'
    },
    'promo.shortheadline.free_shipping': {
        id: 'promo.shortheadline.free_shipping',
        defaultMessage: 'Free Shipping'
    },
    'promo.shortheadline.shipping_on_us': {
        id: 'promo.shortheadline.shipping_on_us',
        defaultMessage: 'Shipping On Us'
    },
    'promo.shortheadline.deal': {
        id: 'promo.shortheadline.deal',
        defaultMessage: 'Black Friday'
    },
    'promo.shortheadline.special_price': {
        id: 'promo.shortheadline.special_price',
        defaultMessage: 'Special Price'
    },
    'promo.shortheadline.bonus': {
        id: 'promo.shortheadline.bonus',
        defaultMessage: 'Bonus'
    },
    'promo.shortheadline.bundle_offer': {
        id: 'promo.shortheadline.bundle_offer',
        defaultMessage: 'Bundle Offer'
    },
    'promo.shortheadline.buy_2_get_1': {
        id: 'promo.shortheadline.buy_2_get_1',
        defaultMessage: 'Buy 2 & Get 1'
    },
    'promo.shortheadline.buy_1_get_1': {
        id: 'promo.shortheadline.buy_1_get_1',
        defaultMessage: 'Buy 1 & Get 1'
    },
    'promo.shortheadline.special_offer': {
        id: 'promo.shortheadline.special_offer',
        defaultMessage: 'Special Offer'
    },
    'promo.shortheadline.final_offer': {
        id: 'promo.shortheadline.final_offer',
        defaultMessage: 'Final Offer'
    },
    'promo.shortheadline.limited_offer': {
        id: 'promo.shortheadline.limited_offer',
        defaultMessage: 'Limited Offer'
    },
    'promo.shortheadline.save_extra': {
        id: 'promo.shortheadline.save_extra',
        defaultMessage: 'Save Extra'
    }
})

export const GENERAL_FORM_VALIDATION_MESSAGES = defineMessages({
    minLength: {
        defaultMessage: `{label} can not be shorter than {n} characters.`,
        id: `use_address_fields.error.min_length_field`
    },
    maxLength: {
        defaultMessage: `{label} can not be longer than {n} characters.`,
        id: `use_address_fields.error.max_length_field`
    }
})

// aspect ratio for item image
export const ITEM_IMAGE_ASPECT = 7 / 9

// delay loading of tracking scripts to avoid blocking page load (in ms)
export const TRACKING_LOADING_DELAY = 8000

// Tracking promise will automatically resolve after this timeout (in ms)
export const TRACKING_TIMEOUT = 1000

// image type to display in pdp cockpit
export const MAIN_IMAGES_IMAGE_TYPE = 'mainview'

// image type to display in emotional gallery component
export const EMOTIONAL_GALLERY_IMAGE_TYPE = 'emotional1'

// number of star-icons available for a rating
export const RATING_MAX_STARS = 5

// value to determine if a halfStar is rendered
export const RATING_HALFSTAR_THRESHOLD = {ceilAt: 3, floorAt: 7}

//Number of words in product usp short description
export const PRODUCT_USP_SHORT_DESC_WORD_COUNT = 33

// image type to display in product tile component
export const PRODUCT_TILE_IMAGE_TYPE = 'mainview'

// Recommended accessories product type
export const RECOMMENDED_ACCESSORIES_TYPE = 1
export const RECOMMENDED_SPARE_PARTS_TYPE = 2
export const RECOMMENDED_ACCESSORY_CONFIGURATIONS_TYPE = 12

// keyboard key numbers
export const EnterKeyNumber = 13

// Month timestamp (1 Month: 2629743000 milliseconds (30.44 days on average))
export const MONTH_MS = 2629743000

// 6 months timestamp in milliseconds
export const NEW_PRODUCT_BADGE_DURATION = 6 * MONTH_MS

// Promotion badge key used for DEAL promotions
export const DEAL_PROMO_KEY = 'promo.shortheadline.deal'

// catalog constants
// STOKKE root category id
export const CATALOG_ROOT_CATEGORY_ID = '14799'

// list of regex that allow loading of external scripts inside cms modules
// NOTE 1: the script must also be allowed in BM csp configuration or helmet-config
// NOTE 2: the loading order is not guaranteed
export const CMS_ALLOWED_EXTERNAL_SCRIPT_SOURCES = [
    // brightcove video player
    /^https:\/\/players.brightcove.net\//i,
    // tiny-slider - temporary until cms modules are updated to use extTns
    /^https:\/\/cdnjs.cloudflare.com\/ajax\/libs\/tiny-slider\//i
]

export const VALIDATE_EMAIL_PATTERN = /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/i

export const MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS = 1
export const MAXIMUM_NUM_OF_RECOMMENDED_PRODUCTS = 12

export const MAXIMUM_NUM_OF_RECOMMENDED_ACCESSORIES = 10

// ID of the category CMS Content slot
export const CATEGORY_BANNER_SLOT_ID = 'cat-banner'
export const CATEGORY_BANNER_PROMO_TOP = 'plp-promo-top'
export const CATEGORY_BANNER_PROMO_MIDDLE = 'plp-promo-middle'

export const EMPLIFI_MAX_LOAD_DELAY = 30000 // 30 seconds

// content for homepage
export const HOME_CONTENT_ASSET_ID = 'root_velocity'

// list of refinements that are not displayed in the selected refinements list
export const HIDDEN_REFINEMENTS = ['cgid', 'htype', 'pmid', 'available-on-web']

export const CRO_PUBLIC_API_NAME = '__cro'

// list of CRO locations
export const CRO_LOCATION = {
    ADD_TO_CART_MODAL: 'addToCartModal',
    CHECKOUT_PAGE: 'checkout',
    CHECKOUT_ORDER_SUMMARY: 'checkoutOrderSummary',
    CHECKOUT_ORDER_SUMMARY_AFTER: 'checkoutOrderSummaryAfter',
    APPCART: 'appcart',
    NAVIGATION_OVERLAY: 'navigationOverlay'
}

export const TILE_CONTEXT_ACCESSORIES = 'matching_acc'
export const TILE_CONTEXT_FANREEL_RELATED_PRODUCTS = 'fanreel_related_products'

export const CRO_EXPERIMENTS = {
    cro63PdpHideRating: {experimentId: 'cro63', variantId: 'variant1'},
    cro65PdpHideDeliveryTime: {experimentId: 'cro65', variantId: 'variant1'},
    cro69CheckoutOrderSummary: {experimentId: 'cro69', variantId: 'variant1'},
    cro71ProductImages: {experimentId: 'cro71', variantId: 'variant1'},
    cro73NewsletterSubscription: {
        experimentId: 'cro73',
        variant1: 'variant1',
        variant2: 'variant2',
        variant3: 'variant3'
    }
}

/**
 * If the page was hidden for at least this interval in milliseconds then
 * on next page show a virtual page load event will be emitted to the data layer
 * @type {number}
 */
export const INTERVAL_TO_EMIT_VIRTUAL_PAGE_EVENT = 30 * 60 * 1000 // 30 minutes

/**
 * Prefix for local storage keys used by BV feedback
 * @type {string}
 */
export const BV_LOCAL_STORAGE_KEY_PREFIX = 'bv-'

/**
 * Default limit for the curalate feed (api default is 10)
 * @type {number}
 * @see https://developers.bazaarvoice.com/v1.0-SocialCommerceAPI/docs/display-media
 */
export const DEFAULT_CURALATE_FEED_LIMIT = 12

/**
 * Default limit for the reviews (api default is 10)
 * @type {number}
 * @see https://developer.bazaarvoice.com/conversations-api/tutorials/display/pagination
 */
export const BV_REVIEWS_LIMIT = 5

/**
 * Navigation tracking level Hero (Header navigation links)
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_HERO_LABEL = 'menu_hero'

/**
 * Navigation tracking level 1
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_1_LABEL = 'menu_level_1'

/**
 * Navigation tracking level 2
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_2_LABEL = 'menu_level_2'

/**
 * Navigation level groups
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_GROUPS_LABEL = 'menu_level_groups'

/**
 * CSP Header Name
 * @type {string}
 */
export const CONTENT_SECURITY_POLICY = 'content-security-policy'

/**
 * Prefix for local storage keys used by CPSDP configuration
 * @type {string}
 */
export const CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX = 'cpsdp_'

/**
 * Maximum number of ids that can be requested in a single getProducts request
 * @type {number}
 */
export const MAX_IDS_PER_GET_PRODUCTS_REQUEST = 23

export const CONFIRMATION_DIALOG_DEFAULT_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Action',
        id: 'confirmation_modal.default.title.confirm_action'
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to continue?',
        id: 'confirmation_modal.default.message.you_want_to_continue'
    },
    primaryActionLabel: {defaultMessage: 'Yes', id: 'confirmation_modal.default.action.yes'},
    alternateActionLabel: {defaultMessage: 'No', id: 'confirmation_modal.default.action.no'}
})

/**
 * Key for dashboard page in my stokke config
 */
export const MY_STOKKE_DASHBOARD_PAGE_ID = 'dashboard'
/**
 * Key for personal information page in my stokke config
 */
export const MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID = 'personal-information'
/**
 * Key for warranty page in my stokke config
 */
export const MY_STOKKE_WARRANTY_PAGE_ID = 'warranty'
/**
 * Key for orders page in my stokke config
 */
export const MY_STOKKE_ORDERS_PAGE_ID = 'orders'
/**
 * Key for addresses page in my stokke config
 */
export const MY_STOKKE_ADDRESSES_PAGE_ID = 'addresses'
/**
 * Key for wishlist page in my stokke config
 */
export const MY_STOKKE_WISHLIST_PAGE_ID = 'wishlist'

/**
 * GTM Register error messages
 */
export const GTM_REGISTER_ERROR_MESSAGES = {
    firstName: 'Invalid first name',
    lastName: 'Invalid last name',
    email: 'Invalid email address',
    password: 'Invalid password'
}

/**
 * Storefront pretection user name
 */
export const STOREFRONT_USERNAME = 'storefront'

/**
 * Local Storage key for enabling logging
 */
export const STOKKE_DEVELOPMENT_LOG = 'stokke-development-log'

/**
 * Timeout in ms for debouncing the cart item quantity change
 * @type {number}
 */
export const CART_ITEM_QUANTITY_DEBOUNCE_WAIT = 750

// Contant to enable PWA kit reset password flow
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2079/files#diff-010df54b1ffccac03a90c74cd8c2556b01ff9e7563a509ef0f046e735c72dae4
export const PWA_RESET_PASSWORD_ENABLED = false
// Constant to enable PWA kit do-not-track notification
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2203
export const PWA_DNT_NOTIFICATION_ENABLED = false

/**
 * Confirmation dialog parameters to confirm removal of item from cart
 */
export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no'
    })
}

export const SUPPORTED_STORE_LOCATOR_COUNTRIES = [
    {
        countryCode: 'US',
        countryName: defineMessage({
            defaultMessage: 'United States',
            id: 'store_locator.dropdown.united_states'
        })
    },
    {
        countryCode: 'DE',
        countryName: defineMessage({
            defaultMessage: 'Germany',
            id: 'store_locator.dropdown.germany'
        })
    }
]

export const DEFAULT_STORE_LOCATOR_COUNTRY = {
    countryCode: 'DE',
    countryName: defineMessage({
        defaultMessage: 'Germany',
        id: 'store_locator.dropdown.germany'
    })
}
export const DEFAULT_STORE_LOCATOR_POSTAL_CODE = '10178'
export const STORE_LOCATOR_DISTANCE = 100
export const STORE_LOCATOR_NUM_STORES_PER_LOAD = 10
export const STORE_LOCATOR_DISTANCE_UNIT = 'km'
// Constant to enable PWA kit store locator
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/1922
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pulls?q=%22store+locator%22+in%3Atitle+is%3Apr+is%3Amerged
export const STORE_LOCATOR_IS_ENABLED = true

export const DEFAULT_DNT_STATE = false
// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array'
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'sourceCode'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'}
    },
    customQualifiers: {
        // Add custom qualifiers here
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    }
}

// Constants for Login
export const LOGIN_TYPES = {
    PASSWORD: 'password',
    PASSWORDLESS: 'passwordless',
    SOCIAL: 'social'
}

export const CUSTOMER_ACTIVATION_TOKEN_QUERY_KEY = 'token'

// Constants for Password Reset
export const RESET_PASSWORD_LANDING_PATH = '/reset-password-landing'

// Constants for Passwordless Login
export const PASSWORDLESS_LOGIN_LANDING_PATH = '/passwordless-login-landing'

export const PASSWORDLESS_ERROR_MESSAGES = [
    /callback_uri doesn't match/i,
    /passwordless permissions error/i,
    /client secret is not provided/i
]

export const PASSWORDLESS_LOGIN_TOKEN_QUERY_KEY = 'pwlstoken'

export const INVALID_TOKEN_ERROR = /invalid token/i

export const USER_NOT_FOUND_ERROR = /user not found/i

/**
 * Maximum quantity of product that can be ordered simultaneously by D2B customers
 * when `d2bMaxQuantity` site pref is not positive number
 * @type {number}
 */
export const MAX_D2B_ORDER_QUANTITY = 999
/**
 * Maximum quantity of product that can be ordered simultaneously by B2C customers
 * when `productPurchaseLimit` site pref is not positive number
 * @type {number}
 */
export const MAX_B2C_ORDER_QUANTITY = 5

/**
 * Product list to hide wishlist icon
 * @type {Array.<string>}
 */
export const HIDE_WISHLIST_TOGGLE = ['583801', '583701', '9800*']

//Constants for accessibility
export const HEADER_MENU_CONTENT_ID = 'header-menu-content'

/**
 * Maximum quantity of products that can receive bonus products
 * @type {number}
 */
export const MAX_BONUS_PRODUCT_QUANTITY = 3

export const CUSTOM_RECOMMENDATION_BESTSELLER_RULE_ID = 'bestSeller'

//This is an API limit and is therefore not configurable
export const NUM_STORES_PER_REQUEST_API_MAX = 200

export const CONTEXT_NOT_PROVIDED = Symbol('CONTEXT_NOT_PROVIDED')

// Use these constants to open cart with specific error message
export const CART_ERROR_FAIL_ORDER = 'fail_order'
export const CART_ERROR_API_ERROR = 'api_error'
export const CART_ERRORS = {
    [CART_ERROR_FAIL_ORDER]: FAIL_ORDER_ERROR_MESSAGE,
    [CART_ERROR_API_ERROR]: API_ERROR_MESSAGE
}
